import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import type { Period } from '@tariff/periods';
import type { Programme, Rate } from '@tariff/authoring';
import { displayRole, rolesUnstated } from '@tariff/roles';
import { useKeyMap, type Binding } from '../lib/keys';
import { Rich, useT } from '../lib/i18n';
// `money`, not `money0`. The grid rounded to whole euros, so a park selling at
// €32,50 saw €33 on the one screen built for entering prices — and had no way to
// tell a rounded display from a rounded price.
import { money, shortDate, toDay } from '../lib/format';

/**
 * The nightly price matrix: accommodations down, periods across.
 *
 * This is the screen an operator lives in for the two days a year they set
 * prices, and it is deliberately NOT a calendar. A calendar is excellent for
 * seeing a year and for the one-off exception; it is hopeless for pricing
 * thirty units across a dozen periods, which is 360 numbers. Park PMSs use a
 * season-by-unit grid for exactly this reason, and the shared period library is
 * what makes the columns exist in the first place.
 *
 * Everything is reachable from the keyboard, because this is data entry and the
 * alternative the operator will otherwise reach for is a spreadsheet. Paste
 * matters most: prices routinely arrive AS a spreadsheet, and being able to
 * select a column and paste it is the difference between five minutes and an
 * afternoon.
 */

export interface MatrixRow {
  versionId: string;
  accommodationId: string;
  /** Full "Name · 2026", used for the cell's accessible name. */
  label: string;
  /** The two halves separately, so a narrow screen can drop the second. */
  name: string;
  versionLabel: string;
  programme: Programme;
  readOnly: boolean;
}

export interface MatrixProps {
  rows: MatrixRow[];
  periods: Period[];
  /**
   * Show the prices without offering to change them.
   *
   * Reception holds `catalogue.read` and nothing that writes: reading a price
   * is most of what a desk does all day, so the grid is exactly the screen they
   * want — and Save, the percentage nudges, the fills and every editable cell
   * on it answer 403. Offering them is offering a button that refuses, which
   * teaches a colleague the software is broken rather than that their account
   * is scoped.
   *
   * A COURTESY, not a control. The server refuses regardless; this only stops
   * somebody wasting a minute finding that out.
   */
  readOnly?: boolean;
  /** Called with every changed programme when the operator saves. */
  onSave: (changed: Array<{ versionId: string; programme: Programme }>) => Promise<void>;
  /**
   * Open one accommodation's night calendar.
   *
   * The grid prices a PERIOD; the calendar prices a NIGHT. Both existed and only
   * one of them was findable — the calendar sits on another tab, behind an
   * accommodation picker, and an operator looking at this screen for "make the
   * Saturday of Ascension dearer" has no reason to think of going there. Given a
   * handler, the accommodation's name becomes the way through.
   */
  onOpenNights?: (row: { accommodationId: string; versionId: string }) => void;
  saving?: boolean;
  /**
   * The season to copy FROM, when this one is empty and a previous one is not.
   *
   * Null when there is nothing to copy — a park's first season, or a year with no
   * cards behind it — and then no button is offered, because a button that cannot
   * work is worse than no button.
   */
  rollForwardFrom?: number | null;
  /** The season being looked at, which is what the copy produces. */
  rollForwardTo?: number;
  onRollForward?: () => void;
  /**
   * Unsaved edits, keyed by version id.
   *
   * Owned by the caller so they survive this component being unmounted — which
   * is what a tab switch does, and what used to discard them silently.
   */
  draft: Record<string, Programme>;
  onDraftChange: (next: Record<string, Programme>) => void;
}

interface Cell { row: number; col: number }

const NIGHTLY_ROLE = 'nightly' as const;

/**
 * Is this a nightly band, for the purposes of DRAWING the grid?
 *
 * A lifted legacy card carries no roles at all — the old format has no such
 * concept — so `role === 'nightly'` is false for every rate on it and the grid
 * showed the park's own cards as "no night price", with nothing to edit. That is
 * what the operator sees on every accommodation that came across from the legacy
 * calculator, which is all of the real ones.
 *
 * Reading is safe; WRITING the guess back is not, because `role: 'nightly'` opts
 * a rate into `flattenNightly` and would reprice a lifted card. So this infers
 * for the reader only, and the write path below still sets the role explicitly
 * on rates this grid creates — a single new band cannot flatten anything, since
 * flattening needs two layers to choose between.
 */
const isNightly = (programme: Programme, rate: Rate): boolean => (
  rolesUnstated(programme) ? displayRole(rate) === NIGHTLY_ROLE : rate.role === NIGHTLY_ROLE
);

/**
 * A night costs one thing mid-week and another at the weekend.
 *
 * ---------------------------------------------------------------------------
 * WHY THE GRID HAS TO KNOW
 * ---------------------------------------------------------------------------
 *
 * The engine has always priced this: two nightly rates over the same window,
 * each restricted to its own weekdays, and a Monday-to-Monday week comes out as
 * five nights at one price and two at the other. What could not be done was
 * ENTERING it — and worse, a card that already had it rendered WRONG here. Two
 * rates of equal width cover the same period, so the grid picked one of them,
 * showed its price as though it were the period's, and edited only that one.
 *
 * A night is named by the day it STARTS. Friday and Saturday nights are the
 * weekend ones; Sunday night belongs to the week ahead. That is what a park
 * means by a weekend supplement, and it is what `arriveOn` says on a one-night
 * rate — the arrival day and the night are the same day.
 */
const WEEKEND: readonly string[] = ['friday', 'saturday'];
const MIDWEEK: readonly string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'];

/** Which nights a cell prices. `all` is a card that does not split its week. */
type DayGroup = 'all' | 'midweek' | 'weekend';

const sameDays = (a: readonly string[] | undefined, b: readonly string[]) => {
  if (!a) return false;
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((d) => set.has(d));
};

/** What a rate's weekday restriction amounts to, as a group. */
function groupOf(rate: Rate): DayGroup {
  const days = rate.arriveOn as readonly string[] | undefined;
  // Absent, or all seven, is "every night" — the two say the same thing and a
  // card lifted from the old format uses both.
  if (!days || days.length === 0 || days.length === 7) return 'all';
  if (sameDays(days, WEEKEND)) return 'weekend';
  if (sameDays(days, MIDWEEK)) return 'midweek';
  // Some other set — a Saturday-only rate, say. Treated as its own thing and
  // left alone rather than half-shown in a column it does not belong to.
  return 'all';
}

/**
 * The nightly rate a cell shows and edits.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT JUST `derivedFrom`
 * ---------------------------------------------------------------------------
 *
 * It used to be exactly that, and the consequence was as bad as it gets. **No
 * rate in a real park carries `derivedFrom`** — nothing in the running product
 * ever sets it, and a card imported or converted from the legacy system has no
 * reason to. So on the screen the dashboard OPENS ON, every cell was blank for a
 * park that sells at €32.50 / €36 / €46 / €52. Not an error: blank.
 *
 * Worse, typing into a blank cell then took the "no existing rate" branch and
 * ADDED a second nightly rate for the same season. The operator saw their number
 * in the grid, saw "Saved", published — and a guest arriving a fortnight earlier
 * was quoted the old price from the rate the operator believed they had edited.
 * A wrong price, published, under a success banner.
 *
 * It survived because `fakeApi.ts` sets `derivedFrom` on its fixtures, so the
 * grid worked perfectly in every test and was blind against every real card.
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 *
 * 1. An explicit `derivedFrom` link wins. It is a statement, and the generator
 *    that makes one means it.
 * 2. Otherwise: the narrowest nightly rate whose window covers the MIDPOINT of
 *    the period.
 *
 * The midpoint rather than the first day because real windows sit a day or two
 * off the period's — a card converted from the legacy system has `2027-03-26 →
 * 2027-05-14` against a period of `2027-03-27 → 2027-05-15` — and matching on an
 * endpoint would miss by that day. The midpoint is inside both.
 *
 * Narrowest wins because that is what the ENGINE does when two rates overlap, so
 * the grid shows the number a guest would actually be charged rather than a
 * different one that happens to sort first.
 */
function rateFor(programme: Programme, period: Period, group: DayGroup = 'all'): Rate | undefined {
  const linked = programme.rates.find(
    (r) => isNightly(programme, r) && r.derivedFrom === period.id && groupOf(r) === group,
  );
  if (linked) return linked;

  const from = toDay(period.from);
  const until = toDay(period.until);
  if (from === null || until === null || until <= from) return undefined;
  const mid = from + Math.floor((until - from) / 2);

  let best: Rate | undefined;
  let bestWidth = Infinity;
  for (const r of programme.rates) {
    if (!isNightly(programme, r)) continue;
    // A rate for OTHER nights than this cell prices. Without this the grid shows
    // one of a split pair as though it were the whole period — which is what it
    // did, silently, for every card that priced weekends differently.
    if (groupOf(r) !== group) continue;
    // A rate that names a DIFFERENT period is spoken for; taking it here would
    // let one period's cell edit another's rate.
    if (r.derivedFrom && r.derivedFrom !== period.id) continue;
    const a = toDay(r.active?.from ?? '');
    const b = toDay(r.active?.until ?? '');
    if (a === null || b === null || b <= a) continue;
    if (mid < a || mid >= b) continue;
    const width = b - a;
    if (width < bestWidth) { best = r; bestWidth = width; }
  }
  return best;
}

/**
 * Periods whose cell is answered by a rate that also answers another period.
 *
 * One rate spanning two seasons is legitimate — a card may price April through
 * June with one line — and it means those two cells are one number. Editing
 * either moves both, so the grid says so rather than letting it surprise
 * somebody. Keyed by period id.
 */
function sharedPeriods(programme: Programme, periods: readonly Period[]): Set<string> {
  const byRate = new Map<Rate, string[]>();
  for (const p of periods) {
    const r = rateFor(programme, p);
    if (!r) continue;
    byRate.set(r, [...(byRate.get(r) ?? []), p.id]);
  }
  const shared = new Set<string>();
  for (const ids of byRate.values()) {
    if (ids.length > 1) for (const id of ids) shared.add(id);
  }
  return shared;
}

function priceFor(programme: Programme, period: Period, group: DayGroup = 'all'): number | null {
  const r = rateFor(programme, period, group);
  return r?.price?.base ?? null;
}

/**
 * Write a price into a programme, creating the nightly rate if needed.
 *
 * A blank clears the rate entirely rather than storing a zero — an
 * accommodation that is simply not sold in a period should have no rate there,
 * not a free one.
 */
function withPrice(
  programme: Programme, period: Period, value: number | null, group: DayGroup = 'all',
): Programme {
  const existing = rateFor(programme, period, group);
  if (value === null) {
    if (!existing) return programme;
    return { ...programme, rates: programme.rates.filter((r) => r !== existing) };
  }
  if (existing) {
    return {
      ...programme,
      rates: programme.rates.map((r) => (r === existing
        ? { ...r, price: { ...r.price, base: value } }
        : r)),
    };
  }
  // Genuinely nothing prices this period, so make a rate. Rare now that an
  // existing one is found by its dates: before, this branch ran for every card in
  // the park and quietly doubled its nightly rates.
  //
  // The id is made unique against what is already there. A collision would make
  // two rates indistinguishable to everything downstream — the diff, the ladder's
  // protected list, the published grid — and `nightly-<date>` is exactly the name
  // a previous run of this function would have chosen.
  const taken = new Set(programme.rates.map((r) => r.id));
  const suffix = group === 'all' ? '' : `-${group}`;
  let id = `nightly-${period.from}${suffix}`;
  for (let n = 2; taken.has(id); n += 1) id = `nightly-${period.from}${suffix}-${n}`;

  const created: Rate = {
    id,
    label: period.label,
    role: NIGHTLY_ROLE,
    active: { from: period.from, until: period.until },
    nights: 1,
    price: { base: value },
    // Linked, so every later lookup is the cheap exact one rather than the
    // date-overlap search.
    derivedFrom: period.id,
    // The nights this price is for. Absent for a card that does not split its
    // week — an unrestricted rate and one listing all seven days mean the same
    // thing, and the shorter one is what a reader expects to see.
    ...(group === 'all' ? {} : { arriveOn: [...(group === 'weekend' ? WEEKEND : MIDWEEK)] as Rate['arriveOn'] }),
  };
  return { ...programme, rates: [...programme.rates, created] };
}

export function Matrix({
  rows, periods, onSave, saving, rollForwardFrom, rollForwardTo, onRollForward, onOpenNights,
  draft: externalDraft, onDraftChange, readOnly,
}: MatrixProps) {
  const t = useT();
  /**
   * Unsaved edits, HELD BY THE CALLER.
   *
   * They used to live here, and switching to the Periods tab unmounted this
   * component and took them with it — the pill said "1 unsaved", the operator
   * looked something up, came back, and their price was gone with no warning and
   * no trace. Silent loss of typed work is the one failure an operator cannot
   * even report, because nothing happened.
   *
   * Lifting it to the caller means the draft outlives this component. It is still
   * this component that decides what goes into it.
   */
  const draft = externalDraft;
  /**
   * Kept as a ref as well as a prop.
   *
   * `setDraft` is called from callbacks memoised on `rows` and `periods`, and a
   * caller-owned value cannot be read through a functional update the way
   * `useState` allows. Reading the ref gives the CURRENT draft rather than the one
   * captured when the callback was built — which is the difference between two
   * edits in one column and the second one overwriting the first.
   */
  const draftRef = useRef(externalDraft);
  draftRef.current = externalDraft;
  const setDraft = useCallback(
    (next: Record<string, Programme> | ((d: Record<string, Programme>) => Record<string, Programme>)) => {
      const value = typeof next === 'function' ? next(draftRef.current) : next;
      // Advanced IMMEDIATELY, not on the next render.
      //
      // A paste writes one cell per call in a synchronous loop. `useState`'s
      // functional updater queues those and applies them in order; a ref refreshed
      // only during render does not, so every iteration read the same starting
      // draft and the last cell was the only one that survived — a pasted column
      // arriving as a single value.
      draftRef.current = value;
      onDraftChange(value);
    },
    [onDraftChange],
  );
  const [cursor, setCursor] = useState<Cell>({ row: 0, col: 0 });
  const [anchor, setAnchor] = useState<Cell | null>(null);
  /**
   * The cell being typed in, and THE TEXT IN IT.
   *
   * The text is the point. This used to hold only the cell's key, and the input
   * was bound to the parsed NUMBER — so a keystroke that does not complete a
   * number could not survive being rendered:
   *
   *   type "3", "8"  → 38
   *   type ","       → parsed to 38, rendered "38", the separator gone
   *   type "7", "5"  → "387" then "3875", saved as €3,875
   *
   * This park sells at €32,50. Typing it produced €3,250, under a "Saved" banner,
   * one Publish away from a guest. Holding the raw text while a cell is being
   * edited is what makes a decimal separator typable at all.
   */
  /**
   * Per row, the periods whose cell is answered by a rate another period uses.
   *
   * Computed here rather than per cell: `rateFor` walks the rate list, and doing
   * that inside the render of a nineteen-column grid is nineteen walks per row.
   */
  const shared = useMemo(
    () => rows.map((r) => sharedPeriods(r.programme, periods)),
    [rows, periods],
  );

  /**
   * Does any card here price its weekends differently?
   *
   * Detected rather than asked, so a card that already splits its week is drawn
   * correctly the moment it is opened — including one lifted from the old format,
   * which is where most of them come from. The toggle then lets an operator turn
   * it on for a card that does not yet.
   */
  const hasSplit = useMemo(
    () => rows.some((r) => r.programme.rates.some(
      (x) => isNightly(r.programme, x) && groupOf(x) !== 'all',
    )),
    [rows],
  );
  const [splitWeek, setSplitWeek] = useState(hasSplit);
  useEffect(() => { if (hasSplit) setSplitWeek(true); }, [hasSplit]);

  /**
   * The columns, which are periods × the nights they price.
   *
   * One per period normally. Two when the week is split, because a period with
   * one number in it cannot say that Friday costs more than Tuesday — and a grid
   * that shows one of the two as though it were the period's price is worse than
   * one that shows both.
   */
  /**
   * Narrowing the columns, for the season that has thirty of them.
   *
   * A park with every school holiday, every bank holiday and a shoulder season
   * either side runs to twenty-odd periods, and the split week doubles that into
   * columns. Scrolling sideways to find "Herfstvakantie" is not editing, and the
   * operator changing one holiday's prices has no use for the other twenty-nine.
   *
   * A FILTER rather than add/remove columns: a column is a period, periods are
   * owned by the Periods tab, and a control here that removed one would either
   * delete a period the rest of the season depends on or invent a second,
   * invisible notion of which periods exist. Hiding is honest and undoable, and
   * the count says out loud that something is hidden — an unexplained short grid
   * is how somebody concludes the season is missing half its dates.
   */
  const [filter, setFilter] = useState('');
  const shownPeriods = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return periods;
    // Dates as well as labels: "2027-07" is how somebody asks for July, and
    // the label of a shoulder period rarely contains a month name.
    return periods.filter((p) => `${p.label} ${p.from} ${p.until}`.toLowerCase().includes(q));
  }, [periods, filter]);

  const columns = useMemo(() => {
    const out: Array<{ period: Period; group: DayGroup; key: string }> = [];
    for (const period of shownPeriods) {
      if (!splitWeek) { out.push({ period, group: 'all', key: period.id }); continue; }
      out.push({ period, group: 'midweek', key: `${period.id}:midweek` });
      out.push({ period, group: 'weekend', key: `${period.id}:weekend` });
    }
    return out;
  }, [shownPeriods, splitWeek]);

  /** Nightly bands per row, so a card that has none can say so itself. */
  const nightlyPerRow = useMemo(
    () => rows.map((r) => r.programme.rates.filter((x) => isNightly(r.programme, x)).length),
    [rows],
  );

  const [editing, setEditing] = useState<
  { key: string; text: string; from: number | null } | null
  >(null);
  const [clip, setClip] = useState<number[][] | null>(null);
  const [status, setStatus] = useState('');
  const hostRef = useRef<HTMLDivElement>(null);

  const programmeOf = useCallback(
    (r: MatrixRow) => draft[r.versionId] ?? r.programme,
    [draft],
  );

  const dirty = Object.keys(draft).length > 0;

  /* Reset local edits whenever the underlying rows are replaced. */
  const signature = rows.map((r) => `${r.versionId}:${r.programme.rates.length}`).join('|');
  useEffect(() => { setDraft({}); }, [signature]);

  const clamp = useCallback((c: Cell): Cell => ({
    row: Math.max(0, Math.min(rows.length - 1, c.row)),
    col: Math.max(0, Math.min(columns.length - 1, c.col)),
  }), [rows.length, columns.length]);

  const move = useCallback((dRow: number, dCol: number, extend = false) => {
    setEditing(null);
    setCursor((c) => {
      const next = clamp({ row: c.row + dRow, col: c.col + dCol });
      if (extend) setAnchor((a) => a ?? c);
      else setAnchor(null);
      return next;
    });
  }, [clamp]);

  const selection = useMemo(() => {
    const a = anchor ?? cursor;
    return {
      r0: Math.min(a.row, cursor.row), r1: Math.max(a.row, cursor.row),
      c0: Math.min(a.col, cursor.col), c1: Math.max(a.col, cursor.col),
    };
  }, [anchor, cursor]);

  const inSelection = (row: number, col: number) =>
    row >= selection.r0 && row <= selection.r1 && col >= selection.c0 && col <= selection.c1;

  const setCell = useCallback((row: number, col: number, value: number | null) => {
    const r = rows[row];
    const c = columns[col];
    if (!r || !c || r.readOnly) return;
    setDraft((d) => ({
      ...d,
      [r.versionId]: withPrice(d[r.versionId] ?? r.programme, c.period, value, c.group),
    }));
  }, [rows, columns]);

  const valueAt = useCallback((row: number, col: number): number | null => {
    const r = rows[row];
    const c = columns[col];
    if (!r || !c) return null;
    return priceFor(programmeOf(r), c.period, c.group);
  }, [rows, columns, programmeOf]);

  const save = useCallback(async () => {
    const changed = Object.entries(draft).map(([versionId, programme]) => ({ versionId, programme }));
    if (changed.length === 0) { setStatus(t('matrix.nothingToSave')); return; }
    await onSave(changed);
    setDraft({});
    setStatus(t('app.savedAccommodations', { count: changed.length }));
  }, [draft, onSave, t]);

  const copy = useCallback(() => {
    const block: number[][] = [];
    for (let r = selection.r0; r <= selection.r1; r++) {
      const line: number[] = [];
      for (let c = selection.c0; c <= selection.c1; c++) line.push(valueAt(r, c) ?? 0);
      block.push(line);
    }
    setClip(block);
    const size = { rows: block.length, cols: block[0]?.length ?? 0 };
    // Also to the system clipboard as TSV, so it pastes into a spreadsheet.
    //
    // `navigator.clipboard` only exists in a SECURE CONTEXT — https, or http on
    // localhost. Served over a plain-http LAN or VPN address it is undefined,
    // and the in-app block copy below still works while spreadsheet interop
    // silently does not. Saying so is the difference between "this tool is
    // broken" and "reach it over the tunnel and it will work".
    const tsv = block.map((l) => l.join('\t')).join('\n');
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(tsv).catch(() => {});
      setStatus(t('matrix.copied', size));
    } else {
      setStatus(t('matrix.copiedInAppOnly', size));
    }
  }, [selection, valueAt, t]);

  const pasteBlock = useCallback((block: number[][]) => {
    block.forEach((line, dr) => {
      line.forEach((v, dc) => setCell(cursor.row + dr, cursor.col + dc, v));
    });
    setStatus(t('matrix.pasted', { rows: block.length, cols: block[0]?.length ?? 0 }));
  }, [cursor, setCell, t]);

  const paste = useCallback(async () => {
    // Prefer the system clipboard so a block copied out of Excel lands
    // correctly; fall back to the in-app copy when the browser refuses.
    try {
      const text = await navigator.clipboard?.readText();
      if (text && text.trim()) {
        const block = text.trim().split(/\r?\n/).map((line) =>
          line.split(/\t|;|,/).map((cellText) => {
            const n = Number(cellText.replace(/[^\d.,-]/g, '').replace(',', '.'));
            return Number.isFinite(n) ? n : 0;
          }));
        pasteBlock(block);
        return;
      }
    } catch { /* clipboard blocked; fall through */ }
    if (clip) pasteBlock(clip);
  }, [clip, pasteBlock]);

  const fillDown = useCallback(() => {
    const from = valueAt(selection.r0, selection.c0);
    if (from === null) return;
    for (let c = selection.c0; c <= selection.c1; c++) {
      const v = valueAt(selection.r0, c);
      for (let r = selection.r0 + 1; r <= selection.r1; r++) setCell(r, c, v);
    }
    setStatus(t('matrix.filledDown'));
  }, [selection, valueAt, setCell, t]);

  const fillRight = useCallback(() => {
    for (let r = selection.r0; r <= selection.r1; r++) {
      const v = valueAt(r, selection.c0);
      for (let c = selection.c0 + 1; c <= selection.c1; c++) setCell(r, c, v);
    }
    setStatus(t('matrix.filledRight'));
  }, [selection, valueAt, setCell, t]);

  const adjust = useCallback((pct: number) => {
    for (let r = selection.r0; r <= selection.r1; r++) {
      for (let c = selection.c0; c <= selection.c1; c++) {
        const v = valueAt(r, c);
        if (v === null) continue;
        setCell(r, c, Math.round(v * (1 + pct / 100) * 100) / 100);
      }
    }
    // The sign is arithmetic, not grammar, so it is glued on here rather than
    // asking every catalogue to remember it.
    setStatus(t('matrix.adjusted', { percent: `${pct > 0 ? '+' : ''}${pct}` }));
  }, [selection, valueAt, setCell, t]);

  const bindings = useMemo<Binding[]>(() => [
    { combo: 'arrowup', label: 'matrix.keyMoveUp', group: 'keys.groupGrid', inFields: true, run: () => move(-1, 0) },
    { combo: 'arrowdown', label: 'matrix.keyMoveDown', group: 'keys.groupGrid', inFields: true, run: () => move(1, 0) },
    { combo: 'arrowleft', label: 'matrix.keyMoveLeft', group: 'keys.groupGrid', inFields: true, run: () => move(0, -1) },
    { combo: 'arrowright', label: 'matrix.keyMoveRight', group: 'keys.groupGrid', inFields: true, run: () => move(0, 1) },
    { combo: 'shift+arrowup', label: 'matrix.keyExtendUp', group: 'keys.groupGrid', inFields: true, run: () => move(-1, 0, true) },
    { combo: 'shift+arrowdown', label: 'matrix.keyExtendDown', group: 'keys.groupGrid', inFields: true, run: () => move(1, 0, true) },
    { combo: 'shift+arrowleft', label: 'matrix.keyExtendLeft', group: 'keys.groupGrid', inFields: true, run: () => move(0, -1, true) },
    { combo: 'shift+arrowright', label: 'matrix.keyExtendRight', group: 'keys.groupGrid', inFields: true, run: () => move(0, 1, true) },
    { combo: 'tab', label: 'matrix.keyNextCell', group: 'keys.groupGrid', inFields: true, run: () => move(0, 1) },
    { combo: 'shift+tab', label: 'matrix.keyPreviousCell', group: 'keys.groupGrid', inFields: true, run: () => move(0, -1) },
    { combo: 'enter', label: 'matrix.keyCommitDown', group: 'keys.groupGrid', inFields: true, run: () => move(1, 0) },
    { combo: 'shift+enter', label: 'matrix.keyCommitUp', group: 'keys.groupGrid', inFields: true, run: () => move(-1, 0) },
    { combo: 'escape', label: 'matrix.keyAbandonEdit', group: 'keys.groupGrid', inFields: true, run: () => setEditing(null) },
    { combo: 'mod+c', label: 'matrix.keyCopy', group: 'keys.groupGrid', inFields: true, run: copy },
    { combo: 'mod+v', label: 'matrix.keyPaste', group: 'keys.groupGrid', inFields: true, run: () => { void paste(); } },
    { combo: 'mod+d', label: 'matrix.keyFillDown', group: 'keys.groupGrid', inFields: true, run: fillDown },
    { combo: 'mod+r', label: 'matrix.keyFillRight', group: 'keys.groupGrid', inFields: true, run: fillRight },
    { combo: 'mod+s', label: 'matrix.keySave', group: 'keys.groupGrid', inFields: true, run: () => { void save(); } },
    { combo: 'delete', label: 'matrix.keyClear', group: 'keys.groupGrid', inFields: true, run: () => {
      for (let r = selection.r0; r <= selection.r1; r++) {
        for (let c = selection.c0; c <= selection.c1; c++) setCell(r, c, null);
      }
    } },
  ], [move, copy, paste, fillDown, fillRight, save, selection, setCell]);

  useKeyMap(bindings);

  /* Keep the focused cell's input actually focused, so typing goes into it. */
  useEffect(() => {
    const el = hostRef.current?.querySelector<HTMLInputElement>(
      `input[data-cell="${cursor.row}:${cursor.col}"]`,
    );
    if (el && document.activeElement !== el) el.focus({ preventScroll: false });
  }, [cursor]);

  if (rows.length === 0 || periods.length === 0) {
    return (
      <div className="stack">
        <p className="note">
          {periods.length === 0 ? t('matrix.noPeriods') : t('matrix.noDrafts')}
        </p>
        {/*
          The journey starts HERE, on the screen the dashboard opens on.

          This was a dead end: "No accommodations with a draft for this season",
          no button, no link, and nothing saying the answer lives on another tab.
          Setting next year's prices almost always means copying this year's and
          changing a few numbers — in the real data 103 of 105 offers were
          identical between seasons — so that is the thing to offer, in one press,
          where somebody looking for it would look.
        */}
        {/*
          Offered even when the season has no PERIODS yet, deliberately. Copying
          cards and defining columns are two different needs: the clone shifts its
          own dates by a year and does not care what the grid can draw. Gating the
          copy on the periods would send an operator to another tab for a reason
          that has nothing to do with what they came here to do.
        */}
        {onRollForward && rollForwardFrom != null && (
          <div className="row">
            <button type="button" className="btn primary" disabled={saving || readOnly} onClick={onRollForward}>
              {/* The season on screen, not the year after the source. With a gap
                  the button read "Copy 2026 to 2027" while the operator was
                  looking at 2028 — and then made 2028 cards. */}
              {t('matrix.rollForward', {
                from: rollForwardFrom,
                // Defaulted rather than left possibly-undefined: the button is
                // only rendered when there IS a source year, and `{to}` printing
                // as "undefined" would be worse than the wrong year.
                to: rollForwardTo ?? rollForwardFrom + 1,
              })}
            </button>
            <span className="note">
              {t('matrix.rollForwardNote', { from: rollForwardFrom })}
            </span>
          </div>
        )}
      </div>
    );
  }

  /**
   * A grid of empty cells is indistinguishable from a broken one.
   *
   * It is empty for a real reason, and the reason is NOT that the card prices
   * by arrangement. A lifted legacy card is full of day tariffs — one-night
   * rates that cannot be extended — and on the campsite they cover 1 April to
   * 1 October with no gaps at all.
   *
   * What is missing is a single band per period to edit. The legacy format
   * layers several day tariffs over the same night — a plain day rate, a
   * midweek one, a weekend one, a holiday one — and the engine takes the
   * cheapest. Eleven of them cover a night in July. Collapsing that into one
   * number per period is a repricing decision, not a display decision, so the
   * grid declines to guess and says so.
   */
  const bandCount = rows.reduce(
    (n, r) => n + r.programme.rates.filter((x) => isNightly(r.programme, x)).length, 0,
  );
  /** A day tariff, in the legacy sense: one night, not extendable. */
  const dayTariffCount = rows.reduce(
    (n, r) => n + r.programme.rates.filter((x) => x.nights === 1 && !x.extend).length, 0,
  );
  const rateCount = rows.reduce((n, r) => n + r.programme.rates.length, 0);

  return (
    <div ref={hostRef}>
      {rows.length > 0 && bandCount === 0 && (
        <div className="banner" role="status" style={{ marginBottom: 12 }}>
          <span>
            <Rich id="matrix.nothingEditable" values={{ count: rows.length }} />{' '}
            {dayTariffCount > 0
              // Several day tariffs can cover one night (plain, midweek,
              // weekend, holiday) and the engine takes the cheapest, so there
              // is no single price per period for a cell to hold. The day
              // tariffs keep quoting exactly as they do now.
              ? t('matrix.dayTariffsExplained', { count: dayTariffCount, rates: rateCount })
              : t('matrix.noNightlyRates', { count: rateCount })}
          </span>
        </div>
      )}

      <div className="row" style={{ marginBottom: 12 }}>
        {/*
          Everything that writes, together, so an account that cannot write
          simply has none of it. Reception reads prices all day and would
          otherwise be shown Save, two percentage nudges and two fills — five
          controls that answer 403.
        */}
        {!readOnly && (
          <>
            <button type="button" className="btn pri" onClick={() => { void save(); }} disabled={!dirty || saving}>
              {saving ? t('matrix.saving') : t('matrix.save')}
            </button>
            <span className={`pill${dirty ? ' warn' : ''}`}>
              {dirty
                ? t('matrix.unsaved', { count: Object.keys(draft).length })
                : t('matrix.allSaved')}
            </span>
            {/* Arithmetic, not words. */}
            <button type="button" className="btn" onClick={() => adjust(3)}>+3%</button>
            <button type="button" className="btn" onClick={() => adjust(-3)}>−3%</button>
            <button type="button" className="btn" onClick={fillDown}>{t('matrix.fillDown')}</button>
            <button type="button" className="btn" onClick={fillRight}>{t('matrix.fillRight')}</button>
          </>
        )}
        {readOnly && <span className="pill">{t('matrix.readOnly')}</span>}
        {/*
          Turning it ON is safe and turning it OFF loses nothing: both prices
          stay on the card and the grid simply stops showing the weekend one. So
          this is a way of LOOKING, not a decision — which is why it is a checkbox
          beside the tools rather than a setting somewhere else.

          Forced on when a card already splits its week: hiding half of what a
          card charges is how the grid used to mis-render exactly these cards.
        */}
        <label className="inline" title={t('matrix.splitWeekNote')}>
          <input
            type="checkbox"
            checked={splitWeek}
            disabled={hasSplit}
            onChange={(e) => setSplitWeek(e.target.checked)}
          />
          {t('matrix.splitWeek')}
        </label>
        {/* Only offered once there are enough columns for finding one to be a
            problem. Below that it is a box that does nothing, taking up the
            space of a tool that does. */}
        {periods.length > 6 && (
          <label className="inline" htmlFor="period-filter">
            <input
              id="period-filter"
              type="search"
              className="period-filter"
              // The toolbar has no room for a visible label beside seven other
              // controls, and a placeholder is not one: it disappears the moment
              // somebody types, so a screen reader announces an unnamed box.
              aria-label={t('matrix.filterPeriods')}
              placeholder={t('matrix.filterPeriods')}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                // The cursor points at a column INDEX, and the column at that
                // index is about to be a different period. Sent home rather
                // than left pointing somewhere plausible and wrong.
                setCursor({ row: 0, col: 0 });
              }}
            />
          </label>
        )}
        {filter.trim() !== '' && (
          <span className="pill warn">
            {t('matrix.filterShowing', {
              shown: shownPeriods.length, total: periods.length,
            })}
            {' '}
            <button
              type="button"
              className="btn link"
              onClick={() => { setFilter(''); setCursor({ row: 0, col: 0 }); }}
            >
              {t('matrix.filterClear')}
            </button>
          </span>
        )}
        <span className="note" aria-live="polite">{status}</span>
      </div>

      {shownPeriods.length === 0 && (
        <p className="note">{t('matrix.filterNoMatch', { query: filter.trim() })}</p>
      )}

      <div className="matrix-wrap">
        <table className="matrix">
          <thead>
            <tr>
              {/* Wrapped so the phone layout can drop it: at 116px the word
                  does not fit, and a column of accommodation names does not
                  need a heading to be understood. */}
              <th className="corner"><span className="cl">{t('matrix.accommodationColumn')}</span></th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  title={t('matrix.periodColumnTitle', { from: c.period.from, until: c.period.until })}
                >
                  <span className="pl">{c.period.label}</span>
                  {/* Which nights this column prices, under the period's name.
                      A night is named by the day it STARTS, so Friday and
                      Saturday nights are the weekend ones. */}
                  {c.group !== 'all' && (
                    <span className="pg">
                      {t(c.group === 'weekend' ? 'matrix.weekendNights' : 'matrix.midweekNights')}
                    </span>
                  )}
                  {/* Through the formatter, so the column head follows the
                      park's date order. Slicing the ISO string gave `MM-DD`,
                      which is month-first — the one order a Dutch park does not
                      read — whatever the setting said. */}
                  <span className="pd">{shortDate(toDay(c.period.from)!)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, row) => (
              <tr key={r.versionId}>
                <th
                  className="rowhead"
                  title={r.readOnly ? t('matrix.rowTitlePublished', { label: r.label }) : r.label}
                >
                  {/* Split so the phone layout can hide the version and the
                      state chip. On a 390px screen the accommodation name is
                      the only part that earns its width — the season is in the
                      panel heading and the state is on the version chips. */}
                  {onOpenNights ? (
                    <button
                      type="button"
                      className="btn link rl"
                      title={t('matrix.openNightsTitle', { name: r.name })}
                      // The VERSION too, not just the accommodation. The
                      // calendar only renders once a version is loaded, and
                      // this row already knows which one it is drawing.
                      onClick={() => onOpenNights({
                        accommodationId: r.accommodationId, versionId: r.versionId,
                      })}
                    >
                      {r.name}
                    </button>
                  ) : (
                    <span className="rl">{r.name}</span>
                  )}
                  <span className="rv"> · {r.versionLabel}</span>
                  {r.readOnly && <span className="chip">{t('matrix.publishedChip')}</span>}
                  {/*
                    A row with NO nightly band at all.

                    The park-wide banner below only fires when every row is like
                    this, so a lodge that sells by the week sat blank and
                    unexplained beside cards that worked — and typing a night
                    price into it did something worse than nothing. The engine
                    tiles a stay the cheapest way it can, so a plausible-looking
                    €80 night turns a €795 week into €560. Correct arithmetic on
                    a number nobody meant to enter.
                  */}
                  {nightlyPerRow[row] === 0 && (
                    <span className="chip warn" title={t('matrix.rowNoNightlyTitle')}>
                      {t('matrix.rowNoNightly')}
                    </span>
                  )}
                </th>
                {columns.map((c, col) => {
                  const p = c.period;
                  const v = valueAt(row, col);
                  const sel = inSelection(row, col);
                  const focused = cursor.row === row && cursor.col === col;
                  // One rate can answer several columns — a card that prices April
                  // through June with one line fills three cells with one number,
                  // and editing any of them moves all three. Legitimate, and
                  // astonishing if it is not said: typing into "Goede Vrijdag"
                  // silently repriced nine columns.
                  const shares = shared[row]?.has(p.id) === true;
                  return (
                    <td
                      key={c.key}
                      className={`${sel ? 'sel' : ''}${shares ? ' shares' : ''}`.trim() || undefined}
                      title={shares ? t('matrix.sharedCell') : undefined}
                    >
                      <input
                        data-cell={`${row}:${col}`}
                        className={`mcell${focused ? ' focused' : ''}${r.readOnly || readOnly ? ' ro' : ''}`}
                        inputMode="decimal"
                        readOnly={r.readOnly || readOnly}
                        aria-label={c.group === 'all'
                          ? t('matrix.cell', { row: r.label, period: p.label })
                          : t('matrix.cellNights', {
                            row: r.label,
                            period: p.label,
                            nights: t(c.group === 'weekend'
                              ? 'matrix.weekendNights' : 'matrix.midweekNights'),
                          })}
                        /*
                          The typed text, but only while it is still the truth.

                          `from` is the model value this text produced. A fill, a
                          paste, a Delete or a percentage adjustment changes the
                          cell from OUTSIDE, and then the half-typed text is stale
                          — the operator selected a column, pressed Delete, and
                          the focused cell went on showing the number they had
                          just typed into it.
                        */
                        value={editing?.key === `${row}:${col}` && editing.from === v
                          ? editing.text
                          : (v === null ? '' : money(v))}
                        onFocus={() => {
                          setCursor({ row, col });
                          // Seeded from the number, not from the formatted text: a
                          // cell that opens showing "€ 32,50" is a cell where the
                          // first keystroke has to delete a currency symbol.
                          setEditing({ key: `${row}:${col}`, text: v === null ? '' : String(v), from: v });
                        }}
                        onChange={(e) => {
                          const text = e.target.value;
                          // The model is kept in step as they type, so the unsaved
                          // count and a fill-down are live. `38.` parses to 38,
                          // which is right; the TEXT is what keeps the separator
                          // on screen until the next digit arrives.
                          const raw = text.replace(/[^\d.,-]/g, '').replace(',', '.');
                          const parsed = raw === '' ? null : Number(raw);
                          const n = parsed !== null && Number.isFinite(parsed) ? parsed : null;
                          setEditing({ key: `${row}:${col}`, text, from: n });
                          setCell(row, col, n);
                        }}
                        onBlur={() => setEditing(null)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Said out loud, because the grid cannot do it and looks like it should.
          Somebody wanting one dearer Saturday will try to type it into a period
          column, and a period column is every night in the period. */}
      {onOpenNights && (
        <p className="note" style={{ marginTop: 10 }}>{t('matrix.oneNightHint')}</p>
      )}
      <p className="note" style={{ marginTop: 10 }}>
        <Rich id="matrix.hint" mark="kbd" />
      </p>
    </div>
  );
}
